import { Badge, ButtonGroup, Typography } from '@statsbomb/kitbag-components';
import PropTypes from 'prop-types';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { styled } from 'styled-components';
import CopyToClipboardWithIcon from '../../../components/Copy';
import PreviewPlayer from '../../../components/PreviewPlayer';
import themeHelper from '../../../utils/themeHelper';

const HLS_IDENTIFIER = '.m3u8';

const mediaConnectFlows = (endpoint) =>
  endpoint.media_package_channel?.media_live_channel?.media_live_inputs[0]
    ?.media_connect_flows || [];

const mediaLiveInputSources = (endpoint) => {
  const sources =
    endpoint.media_package_channel?.media_live_channel?.media_live_inputs[0]
      ?.Sources;
  const destinations =
    endpoint.media_package_channel?.media_live_channel?.media_live_inputs[0]
      ?.Destinations;
  if (sources && sources.length > 0) {
    return sources;
  }
  if (destinations && destinations.length > 0) {
    return destinations;
  }
  return [];
};

const getInput = (endpoint) => {
  if (mediaConnectFlows(endpoint) && mediaConnectFlows(endpoint).length > 0) {
    return 'MediaConnect Flow';
  }
  if (
    mediaLiveInputSources(endpoint) &&
    mediaLiveInputSources(endpoint).length > 0
  ) {
    return 'MediaLive Sources';
  }
  return 'No sources';
};

const UrlsContainer = styled.div`
  display: flex;
  gap: ${themeHelper.spacing(2)};
  justify-content: left;
  padding: ${themeHelper.spacing()};
`;
const StreamContainer = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  max-width: 35px;
`;

export function State({ id, state, location }) {
  const alive = (
    <Badge type="success">
      <FormattedMessage defaultMessage="ACTIVE" id="18ff7" />
    </Badge>
  );
  const stopped = (
    <Badge type="error">
      <FormattedMessage defaultMessage="IDLE" id="a5daf" />
    </Badge>
  );

  const states = {
    ACTIVE: alive,
    IDLE: stopped,
    RUNNING: alive,
    STANDBY: stopped,
    STARTING: <span key={`${id}-${location}-state-starting`}>{state}</span>,
    STOPPING: <span key={`${id}-${location}-state-stopping`}>{state}</span>,
  };
  return states[state] ?? null;
}

State.propTypes = {
  id: PropTypes.string.isRequired,
  location: PropTypes.string.isRequired,
  state: PropTypes.string.isRequired,
};

export function BroadcasterConnectionSummary({ endpoint, messageWhenEmpty }) {
  return (
    <>
      {mediaConnectFlows(endpoint)?.length > 0
        ? mediaConnectFlows(endpoint).map((flow) => (
            <div key={`${flow.FlowArn}-BroadcasterConnectionSummary`}>
              <State
                id={flow.FlowArn}
                state={flow.Status}
                location="BroadcasterConnectionSummary"
              />
            </div>
          ))
        : messageWhenEmpty}
    </>
  );
}

BroadcasterConnectionSummary.propTypes = {
  endpoint: PropTypes.shape({
    DomainName: PropTypes.string,
    media_package_channel: PropTypes.shape({
      endpoints: PropTypes.arrayOf(
        PropTypes.shape({
          Id: PropTypes.string.isRequired,
          Url: PropTypes.string.isRequired,
        }),
      ),
      media_live_channel: PropTypes.shape({
        Id: PropTypes.string,
        media_live_inputs: PropTypes.arrayOf(PropTypes.shape({})),
        Name: PropTypes.string,
        State: PropTypes.string,
      }),
    }),
  }).isRequired,
  messageWhenEmpty: PropTypes.string,
};
BroadcasterConnectionSummary.defaultProps = {
  messageWhenEmpty: '',
};

export function ChannelSummary({ endpoint }) {
  return (
    <State
      id={endpoint.media_package_channel?.media_live_channel?.Id}
      state={endpoint.media_package_channel?.media_live_channel?.State}
      location="ChannelSummary"
    />
  );
}

ChannelSummary.propTypes = {
  endpoint: PropTypes.shape({
    DomainName: PropTypes.string,
    media_package_channel: PropTypes.shape({
      endpoints: PropTypes.arrayOf(
        PropTypes.shape({
          Id: PropTypes.string.isRequired,
          Url: PropTypes.string.isRequired,
        }),
      ),
      media_live_channel: PropTypes.shape({
        Id: PropTypes.string,
        media_live_inputs: PropTypes.arrayOf(PropTypes.shape({})),
        Name: PropTypes.string,
        State: PropTypes.string,
      }),
    }),
  }).isRequired,
};

export function InputFullDetails({ endpoint }) {
  return (
    <>
      <Typography variant="headingSmall">{getInput(endpoint)}</Typography>
      {mediaConnectFlows(endpoint).map((flow) => (
        <React.Fragment key={flow.FlowArn}>Name: {flow.Name}</React.Fragment>
      ))}
      {mediaLiveInputSources(endpoint).map((source) => (
        <React.Fragment key={source.Url}>{source.Url}</React.Fragment>
      ))}
    </>
  );
}

InputFullDetails.propTypes = {
  endpoint: PropTypes.shape({
    DomainName: PropTypes.string,
    media_package_channel: PropTypes.shape({
      endpoints: PropTypes.arrayOf(
        PropTypes.shape({
          Id: PropTypes.string.isRequired,
          Url: PropTypes.string.isRequired,
        }),
      ),
      media_live_channel: PropTypes.shape({
        Id: PropTypes.string,
        media_live_inputs: PropTypes.arrayOf(PropTypes.shape({})),
        Name: PropTypes.string,
        State: PropTypes.string,
      }),
    }),
  }).isRequired,
};

export function MediaLiveInputFullDetails({ endpoint }) {
  return (
    <div>
      <Typography variant="headingSmall">
        <FormattedMessage
          description="MediaLive Input"
          defaultMessage="MediaLive Input"
          id="76591"
        />
      </Typography>
      {endpoint.media_package_channel?.media_live_channel?.media_live_inputs?.map(
        (input) => input.Name,
      )}
    </div>
  );
}

MediaLiveInputFullDetails.propTypes = {
  endpoint: PropTypes.shape({
    DomainName: PropTypes.string,
    media_package_channel: PropTypes.shape({
      endpoints: PropTypes.arrayOf(
        PropTypes.shape({
          Id: PropTypes.string.isRequired,
          Url: PropTypes.string.isRequired,
        }),
      ),
      media_live_channel: PropTypes.shape({
        Id: PropTypes.string,
        media_live_inputs: PropTypes.arrayOf(PropTypes.shape({})),
        Name: PropTypes.string,
        State: PropTypes.string,
      }),
    }),
  }).isRequired,
};

export function MediaLiveChannelFullDetails({ endpoint }) {
  return (
    <>
      <Typography variant="headingSmall">
        <FormattedMessage
          description="MediaLive Channel"
          defaultMessage="MediaLive Channel"
          id="d3a39"
        />
      </Typography>
      {endpoint.media_package_channel?.media_live_channel?.Name}
      <br />
      <State
        id={endpoint.media_package_channel?.media_live_channel?.Id}
        state={endpoint.media_package_channel?.media_live_channel?.State}
        location="MediaLiveChannelFullDetails"
      />
    </>
  );
}

MediaLiveChannelFullDetails.propTypes = {
  endpoint: PropTypes.shape({
    DomainName: PropTypes.string,
    media_package_channel: PropTypes.shape({
      endpoints: PropTypes.arrayOf(
        PropTypes.shape({
          Id: PropTypes.string.isRequired,
          Url: PropTypes.string.isRequired,
        }),
      ),
      media_live_channel: PropTypes.shape({
        Id: PropTypes.string,
        media_live_inputs: PropTypes.arrayOf(PropTypes.shape({})),
        Name: PropTypes.string,
        State: PropTypes.string,
      }),
    }),
  }).isRequired,
};

export function CloudFrontFullDisplay({ endpoint }) {
  return (
    <div>
      <Typography variant="headingSmall">
        <FormattedMessage
          description="Cloudfront URL"
          defaultMessage="Cloudfront URL"
          id="800b0"
        />
      </Typography>
      {endpoint.DomainName}
    </div>
  );
}

CloudFrontFullDisplay.propTypes = {
  endpoint: PropTypes.shape({
    DomainName: PropTypes.string,
    media_package_channel: PropTypes.shape({
      endpoints: PropTypes.arrayOf(
        PropTypes.shape({
          Id: PropTypes.string.isRequired,
          Url: PropTypes.string.isRequired,
        }),
      ),
      media_live_channel: PropTypes.shape({
        Id: PropTypes.string,
        media_live_inputs: PropTypes.arrayOf(PropTypes.shape({})),
        Name: PropTypes.string,
        State: PropTypes.string,
      }),
    }),
  }).isRequired,
};

export function FullUrls({ endpoint }) {
  const intl = useIntl();

  const endpointDisplayTypes = {
    hls: intl.formatMessage({ defaultMessage: 'HLS', id: '03688' }),
    unknown: intl.formatMessage({
      defaultMessage: 'Unknown Protocol',
      description: 'Unknown endpoint protocol',
      id: '04974',
    }),
  };

  const endpointDisplayValues = (url) => {
    if (url.includes(HLS_IDENTIFIER)) {
      return {
        displayValue: endpointDisplayTypes.hls,
        urlType: 'MPD',
      };
    }

    return {
      displayValue: endpointDisplayTypes.unknown,
      urlType: 'UNKNOWN',
    };
  };

  const endpointsWithDisplayValue = endpoint.media_package_channel?.endpoints
    ?.filter((currentEndpoint) =>
      currentEndpoint.Url.toLowerCase().includes(HLS_IDENTIFIER),
    )
    .map((currentEndpoint) => ({
      ...currentEndpoint,
      ...endpointDisplayValues(currentEndpoint.Url),
    }));

  const byEndpointDisplayValue = (a, b) => a.urlType.localeCompare(b.urlType);
  const sortedEndpointsWithDisplayValue = (
    endpointsWithDisplayValue ?? []
  ).sort(byEndpointDisplayValue);

  const fullUrls = (
    <UrlsContainer>
      {sortedEndpointsWithDisplayValue.map((channelEndpoint) => {
        const splitVals = channelEndpoint.Url.split('/');
        const fullUrl = `https://${endpoint.DomainName}/out/v1/${
          splitVals[splitVals.length - 2]
        }/${splitVals[splitVals.length - 1]}`;

        const numberOfEndpoints =
          endpoint.media_package_channel?.endpoints?.length || 1;

        return (
          <StreamContainer
            key={channelEndpoint.Id}
            $numberOfEndpoints={numberOfEndpoints}
          >
            <Badge>{channelEndpoint.displayValue}</Badge>
            <ButtonGroup>
              <CopyToClipboardWithIcon text={fullUrl} />
              <PreviewPlayer
                key={fullUrl}
                title={intl.formatMessage(
                  { defaultMessage: 'Preview {name}', id: '261d5' },
                  {
                    name: channelEndpoint.displayValue,
                  },
                )}
                url={fullUrl}
              />
            </ButtonGroup>
          </StreamContainer>
        );
      })}
    </UrlsContainer>
  );
  return fullUrls;
}

FullUrls.propTypes = {
  endpoint: PropTypes.shape({
    DomainName: PropTypes.string,
    media_package_channel: PropTypes.shape({
      endpoints: PropTypes.arrayOf(
        PropTypes.shape({
          Id: PropTypes.string.isRequired,
          Url: PropTypes.string.isRequired,
        }),
      ),
      media_live_channel: PropTypes.shape({
        Id: PropTypes.string,
        media_live_inputs: PropTypes.arrayOf(PropTypes.shape({})),
        Name: PropTypes.string,
        State: PropTypes.string,
      }),
    }),
  }).isRequired,
};

export default {
  BroadcasterConnectionSummary,
  ChannelSummary,
  CloudFrontFullDisplay,
  FullUrls,
  InputFullDetails,
  MediaLiveChannelFullDetails,
  MediaLiveInputFullDetails,
};
